import {
  ActionIcon,
  Input,
  Flex,
  Text,
  type InputProps,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { type ReactNode, useEffect, useMemo, useRef } from "react";

import useSavedCursorPosition from "../../hooks/useSavedCursorPosition";
import { useTheme } from "src/hooks";
import { useDispatch, useSelector } from "../../redux/hooks";
import useAmbPrevData from "../hooks/useAmbPrevData";
import { useDynamicTextWidth } from "../hooks/useDynamicTextWidth";
import { useInputProps } from "../hooks/useInputProps";
import useNMEAAutomated from "../hooks/useNMEAAutomated";
import useReadonlyClass from "../hooks/useReadonlyClass";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import { getClassName, getPreparedClassname } from "./CustomInput.utils";
import useAmbHeightCalc from "./useAmbHeightCalc";
import useAmbTotals from "./useAmbTotals";
import useAmbVoyageTotals from "./useAmbVoyageTotals";
import { useArbemaTotals } from "./useArbemaTotals";
import useEngineSelectData from "./useEngineSelectData";
import { useOnyxROBTotal } from "./useOnyxROBTotal";
import { useOnyxTankConvert } from "./useOnyxTankM3";
import { useSakuraTotalSum } from "./useSakuraPOBTotal";
import { useAmlCalcTotal, useAmlCalcPerc } from "./useAmlCalc";
import { useMoonFWDiff } from "./useMoonFWDiff";
import useDPFHoursCalculation from "./useDPFHoursCalculation";
import useFWCalculation from "./useFWCalculation";
import { useValorCalc } from "./useValorCalc";
import { setCellProperty } from "src/redux/slices/generatorSlice";
import CustomConverter from "../CustomConverter/CustomConverter";
import { IconEdit, IconX } from "@tabler/icons-react";

interface ICustomInput extends InputProps {
  value: any;
  id: string;
  textarea?: boolean;
  required?: boolean;
  width?: string;
  suffix?: string | ReactNode;
  exclusive?: string;
  domId?: string;
  class?: string;
  feature?: string;
  defaultValue?: string;
  rowIndex?: number;
  subrowIndex?: number;
  colIndex?: number;
  grayed?: boolean;
  prefix?: string;
  isOverlay?: boolean;
  readonly?: boolean;
  searchEntries?: string;
  tag?: string;
  // section context for vertical tabbing
  sectionIndex?: number;
  // ambassador specific properties
  ambGetPreviousData?: string;
  ambHeightCalc?: string;
  ambVoyageTotals?: string;
  ambSteamingTotals?: string;
  ambSpeedTotals?: string;

  additionalOnChange?: (value: any, id?: string) => void;
  additionalOnBlur?: (e: any) => void;

  name?: string;
  source?: "auto" | "manual";
  originalValue?: number;

  amlGroup?: string;
  amlTotal?: string;
  amlCalc?: string;

  previousTotal?: number;
  manualEdit?: boolean;

  convMT?: boolean;
  textWidth?: string;

  // numeric-mode specific
  numeric?: boolean;
  autoMinus?: boolean;
  sectionTag?: string;
}

const CustomInput = ({
  value,
  id,
  textarea,
  required,
  width,
  suffix,
  exclusive,
  domId,
  class: className,
  feature,
  defaultValue,
  rowIndex,
  subrowIndex,
  colIndex,
  grayed,
  prefix,
  readonly,
  isOverlay,
  searchEntries,
  sectionIndex,
  ambGetPreviousData,
  ambHeightCalc,
  ambVoyageTotals,
  ambSpeedTotals,
  ambSteamingTotals,
  tag,
  additionalOnChange,
  additionalOnBlur,
  name,
  source,
  originalValue,
  amlGroup,
  amlTotal,
  amlCalc,
  previousTotal,
  convMT,
  textWidth,
  manualEdit,
  numeric,
  autoMinus,
  sectionTag,
  ...props
}: ICustomInput) => {
  const { features } = useSelector(state => state.generatorSlice);
  const { getEngineSelectValue } = useEngineSelectData();
  const dispatch = useDispatch();
  const theme = useTheme() as any;
  const readOnlyClass = useReadonlyClass({
    readonly: readonly ?? props.disabled,
    grayed,
  });
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const {
    onChange: onInputChange,
    onBlur: onInputBlur,
    setValue,
    ...inputProps
  } = useInputProps(id, value ?? defaultValue, required, exclusive);

  // onyx tank specific hooks - adds classes and handles conversion
  useOnyxTankConvert({ id, tag, tankGroup: `tank-${rowIndex?.toString()}` });

  // onyx ROB specific hooks - calculates percentage from ROB value and adds classes
  useOnyxROBTotal({ id, tag, robGroup: `rob-${rowIndex?.toString()}` });

  // sakura total hook - only for TOTAL field
  useSakuraTotalSum({ tag: domId || className, id, setValue });

  // moonrise fw diff hook
  useMoonFWDiff(tag ?? "", previousTotal, id, setValue);

  // ambassador logbook specific hooks for tooling
  useAmbHeightCalc({ ambHeightCalc, id, setValue });
  useAmbPrevData({ id, dataTag: ambGetPreviousData, value: inputProps.value });
  useAmbTotals({
    id,
    setValue,
    manualEdit: manualEdit ?? false,
    ambVoyageTotals,
    ambSteamingTotals,
    ambSpeedTotals,
  });

  useAmbVoyageTotals({
    id,
    setValue,
    rowIndex: rowIndex ?? 0,
    tag,
  });

  useArbemaTotals({
    id,
    tag,
    defaultValue: originalValue,
  });

  useAmlCalcTotal({
    id,
    setValue,
    amlGroup,
    amlTotal,
  });

  useAmlCalcPerc({
    id,
    setValue,
    amlCalc,
    feature,
    rowIndex: rowIndex ?? 0,
  });

  // DPF hours calculation hook
  useDPFHoursCalculation({ id, tag, setValue });

  // Fresh Water calculation hook
  useFWCalculation({ id, tag, setValue });

  // Valor Total Fuel calculation hook
  useValorCalc({ sectionTag, tag, rowIndex, id, setValue });

  // useAmbTotalLog({
  //   defaultValue: originalValue ?? parseFloat(defaultValue ?? "") ?? 0,
  //   id,
  //   setValue,
  //   className: ambVoyageTotals === "totalLog" ? "totalLog" : className,
  //   rowIndex,
  //   isOverlay,
  //   inputRef,
  // });

  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue,
    name,
  });

  const {
    handleFocus,
    handleBlur: handleCursorBlur,
    setCursorPosition,
  } = useSavedCursorPosition(id, inputRef);

  // Get logbook refs context props for navigation
  const logbookRefsProps = useLogbookRefsContext(id);

  useEffect(() => {
    if (isOverlay) return;
    const valueId = feature === "autoday" ? "autoday" : domId;
    if (feature === "contactNumber" && features.contactNumber !== undefined) {
      setValue(id, features.contactNumber);
      return;
    }
    if (valueId && features[valueId] !== undefined)
      setValue(id, features[valueId]);

    const popTags = ["full_name_caps"];
    if (tag && popTags.includes(tag) && features[tag] !== undefined) {
      setValue(id, features[tag]);
    }
  }, [domId, features, id, isOverlay, feature, setValue, tag]);

  const minInputWidth = useMemo(() => {
    if (!width || width.includes("%")) return 200;
    return Number(width) * 10;
  }, [width]);

  const {
    dynamicWidth: dynamicInputWidth,
    sizerRef,
    updateDynamicWidth,
  } = useDynamicTextWidth({
    enabled: textWidth === "dynamic",
    value: inputProps.value,
    minWidth: minInputWidth,
    inputRef,
  });

  const prevValue = useRef((value ?? defaultValue)?.toString());

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    if (inputValue === "" || /^[+-]?\d*(\.\d*)?$/.test(inputValue)) {
      if (
        autoMinus &&
        prevValue.current?.toString() === "" &&
        ![".", ",", "-"].includes(inputValue)
      ) {
        inputValue = `-${inputValue}`;
      }
      event.target.value = inputValue;
      prevValue.current = inputValue;
      setCursorPosition();
      additionalOnChange?.(inputValue, id);
      onInputChange(inputValue);
    }
  };

  // shouldHighlightValue is currently a no-op stub, kept as-is from CustomNumberInput
  const shouldHighlightValue = (
    highlightTag: string | undefined,
    highlightValue: any,
  ): boolean => {
    // if (highlightTag === "valorC" && parseFloat(highlightValue) < 0) return true;
    return false;
  };

  const handleInputChange = (e?: any, withoutTimeout?: boolean) => {
    if (numeric) {
      if (e) handleNumericChange(e);
      return;
    }
    if (isOverlay) return;
    setCursorPosition();
    if (textWidth === "dynamic" && textarea && e?.target) {
      updateDynamicWidth(
        `${e.target.value ?? ""}`,
        e.target as HTMLTextAreaElement,
      );
    }
    if (e) {
      onInputChange(e);
      additionalOnChange?.(e);
    }
  };

  const canManuallyEdit =
    ambVoyageTotals ?? ambSpeedTotals ?? ambSteamingTotals;
  const isReadOnly =
    (ambVoyageTotals ?? ambSpeedTotals ?? ambSteamingTotals) &&
    !canManuallyEdit;

  const setManualEdit = (value: boolean) => {
    if (canManuallyEdit) {
      dispatch(
        setCellProperty({
          sectionIndex,
          rowIndex: rowIndex ?? 0,
          cellId: id,
          change: "manualEdit",
          property: { manualEdit: value },
        }),
      );
    }
  };

  const handleBlur = (e: any) => {
    if (numeric) {
      handleNMEAChange(inputProps.value);
      onInputBlur();
      handleCursorBlur(e);
      additionalOnBlur?.(e);
      return;
    }
    if (isOverlay) return;
    onInputBlur();
    handleNMEAChange(inputProps.value);
    handleCursorBlur(e);
    additionalOnBlur?.(e);
    setManualEdit(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Call logbook refs context keyDown handler first for navigation
    logbookRefsProps.onKeyDown(e);

    // Restrict input to numbers for tank and ROB fields
    const isOnyxField =
      tag === "onyxTankValue" ||
      tag === "onyxTankM3" ||
      (tag && tag.startsWith("OnyxROBTotal"));

    if (isOnyxField) {
      // Allow: backspace, delete, tab, escape, enter, period, minus
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        ".",
        "-",
      ];
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      const allowedCtrlKeys = ["a", "c", "v", "x", "z"];

      if (
        allowedKeys.includes(e.key) ||
        (e.ctrlKey && allowedCtrlKeys.includes(e.key.toLowerCase())) ||
        // Allow numbers (both regular and numpad)
        (!isNaN(Number(e.key)) && e.key !== " ")
      ) {
        return;
      }

      // Prevent default for all other keys
      e.preventDefault();
    }
  };

  const disabled = useMemo(
    () =>
      readonly ??
      props.disabled ??
      inputProps.disabled ??
      isOverlay ??
      Boolean(
        className && features[`${className}`] && className?.includes("grey"),
      ) ??
      // Disable OnyxPercTotal fields - they should only be calculated, not manually edited
      (tag && tag.startsWith("OnyxPercTotal")),
    [props.disabled, className, features, inputProps, isOverlay, readonly, tag],
  );

  const parsedClassName = getPreparedClassname(
    ambHeightCalc,
    tag,
    ambVoyageTotals,
    amlGroup,
    amlCalc,
    className,
    domId,
    colIndex,
    rowIndex ?? 0,
    getEngineSelectValue,
  );

  if (textarea) {
    return (
      <div
        style={{
          position: "relative",
          width: textWidth === "dynamic" ? `${dynamicInputWidth}px` : "100%",
        }}>
        {textWidth === "dynamic" && (
          <span
            ref={sizerRef}
            aria-hidden
            style={{
              position: "absolute",
              visibility: "hidden",
              whiteSpace: "pre",
              pointerEvents: "none",
            }}
          />
        )}
        <Textarea
          className={` ${getClassName(parsedClassName, rowIndex)}${isOverlay ? `_overlay ${readOnlyClass}` : ` ${readOnlyClass}`}`}
          autosize
          minRows={1}
          miw={textWidth === "dynamic" ? dynamicInputWidth : 25}
          wrap={textWidth === "dynamic" ? "off" : "soft"}
          {...props}
          {...inputProps}
          ref={el => {
            inputProps.ref(el);
            inputRef.current = el;
            logbookRefsProps.ref(el);
          }}
          styles={{
            input: {
              paddingTop: "6px !important",
              paddingBottom: "6px !important",
              whiteSpace: textWidth === "dynamic" ? "pre" : undefined,
              ...inputProps.styles.input,
            },
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
        />
      </div>
    );
  }
  if (numeric) {
    return (
      <Input
        id={id}
        className={getClassName(tag, rowIndex)}
        miw={Number(width) * 10}
        {...props}
        {...inputProps}
        ref={el => {
          inputProps.ref(el);
          inputRef.current = el;
          logbookRefsProps.ref(el);
        }}
        styles={{
          input: {
            color: shouldHighlightValue(tag, value)
              ? theme.colors.redText
              : "inherit",
          },
        }}
        onKeyDown={logbookRefsProps.onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleInputChange}
        rightSection={suffix}
        rightSectionProps={{
          style: {
            width: "max-content",
            padding: "0 10px",
          },
        }}
      />
    );
  }

  // tag &&
  //   console.log({
  //     tag,
  //     props,
  //   });

  const showConverter = (false || convMT) && !disabled; // add additional conditions if needed
  const mtProps = {
    rows: [
      {
        fields: ["Y [m3]", "Z [mt/m3]"],
        divisors: [1, 1],
        result: "X [mt]",
      },
      {
        fields: ["Y [m3]", "Z [kg/m3]"],
        divisors: [1, 1000],
        result: "X [mt]",
      },
    ],
    type: "multiply" as const,
    fieldId: id,
  };
  // now supports percentages
  width = width
    ? width.includes("%")
      ? width
      : `${Number(width) * 10}px`
    : width;
  const parsedIdx: any = ambHeightCalc ?? rowIndex ?? 0;
  return (
    <Input
      className={` ${getClassName(parsedClassName, parsedIdx)}${isOverlay ? `_overlay ${readOnlyClass}` : ` ${readOnlyClass}`}`}
      miw={width}
      rightSection={
        (showConverter ?? suffix ?? canManuallyEdit) ? (
          <Flex gap={4}>
            {showConverter && <CustomConverter {...mtProps} />}
            {canManuallyEdit && (
              <Tooltip
                label={manualEdit ? "Remove manual value" : "Calculated value"}
                withArrow>
                <ActionIcon
                  size="sm"
                  color={manualEdit ? "red" : "green"}
                  onClick={() => manualEdit && setManualEdit(false)}>
                  {manualEdit ? <IconX size={16} /> : <IconEdit size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
            {suffix}
          </Flex>
        ) : undefined
      }
      onChange={handleInputChange}
      rightSectionProps={
        (showConverter ?? suffix ?? canManuallyEdit)
          ? {
            style: {
              display: "flex",
              width: "max-content",
              padding: "0 10px",
              color: "var(--mantine-color-dimmed)",
            },
          }
          : undefined
      }
      {...props}
      {...inputProps}
      placeholder={prefix ?? ""}
      ref={el => {
        inputProps.ref(el);
        inputRef.current = el;
        logbookRefsProps.ref(el);
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      readOnly={!!isReadOnly}
      {...(amlCalc && { "data-aml": amlCalc })}
    />
  );
};

export default CustomInput;
